import { propertyType, t } from '@blocksuite/affine/blocks/database';
import {
  normalizeAttachmentCell,
  type NormalizedAttachmentCell,
} from '@streamlain/database-identity';
import zod from 'zod';

export const fileColumnType = propertyType('attachment');

const RawFileItemSchema = zod.object({
  id: zod.string().optional(),
  name: zod.string(),
  mime: zod.string().optional(),
  size: zod.number().nullable().optional(),
  order: zod.string(),
});

export const FileItemSchema = RawFileItemSchema.extend({
  id: zod.string(),
  refId: zod.string(),
});
export type FileItemType = zod.TypeOf<typeof FileItemSchema>;
const FileCellRawValueTypeSchema = zod.record(zod.string(), RawFileItemSchema);
const NormalizedAttachmentFileSchema = zod.object({
  blobId: zod.string(),
  id: zod.string(),
  mime: zod.string(),
  name: zod.string(),
  size: zod.number().nullable(),
});
export const FileCellJsonValueTypeSchema: zod.ZodType<NormalizedAttachmentCell> =
  zod.object({
    blobIds: zod.array(zod.string()),
    count: zod.number(),
    filenames: zod.array(zod.string()),
    files: zod.array(NormalizedAttachmentFileSchema),
    isEmpty: zod.boolean(),
    mimeTypes: zod.array(zod.string()),
  });
export type FileCellRawValueType = zod.TypeOf<
  typeof FileCellRawValueTypeSchema
>;
export type FileCellJsonValueType = zod.TypeOf<
  typeof FileCellJsonValueTypeSchema
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function getFileCellItems(value: unknown): FileItemType[] {
  if (!isRecord(value)) {
    return [];
  }

  return Object.entries(value).flatMap(([refId, rawItem]) => {
    const parsed = RawFileItemSchema.safeParse(rawItem);
    return parsed.success
      ? [{ ...parsed.data, id: parsed.data.id ?? refId, refId }]
      : [];
  });
}

export function normalizeFileCellItems(value: unknown): FileCellRawValueType {
  const normalized: FileCellRawValueType = {};
  for (const item of getFileCellItems(value)) {
    const { refId, ...rawItem } = item;
    normalized[refId] = rawItem;
  }
  return normalized;
}

export function renameFileCellItem(
  value: FileCellRawValueType,
  refId: string,
  nextName: string
): FileCellRawValueType | null;
export function renameFileCellItem(
  value: unknown,
  refId: string,
  nextName: string
): Record<string, unknown> | null;
export function renameFileCellItem(
  value: unknown,
  refId: string,
  nextName: string
): Record<string, unknown> | null {
  if (!isRecord(value) || !isRecord(value[refId])) {
    return null;
  }

  return {
    ...value,
    [refId]: {
      ...value[refId],
      name: nextName,
    },
  };
}

export function replaceFileCellItemBlob(
  value: FileCellRawValueType,
  refId: string,
  replacement: {
    blobId: string;
    mimeType: string;
    size: number;
    name?: string;
  }
): FileCellRawValueType | null;
export function replaceFileCellItemBlob(
  value: unknown,
  refId: string,
  replacement: {
    blobId: string;
    mimeType: string;
    size: number;
    name?: string;
  }
): Record<string, unknown> | null {
  if (!isRecord(value) || !isRecord(value[refId])) {
    return null;
  }

  return {
    ...value,
    [refId]: {
      ...value[refId],
      id: replacement.blobId,
      mime: replacement.mimeType,
      size: replacement.size,
      ...(replacement.name ? { name: replacement.name } : {}),
    },
  };
}

export const filePropertyModelConfig = fileColumnType.modelConfig({
  name: 'Attachment',
  propertyData: {
    schema: zod.object({}),
    default: () => ({}),
  },
  rawValue: {
    schema: FileCellRawValueTypeSchema,
    default: () => ({}) as FileCellRawValueType,
    fromString: () => ({
      value: {},
    }),
    toString: ({ value }) =>
      getFileCellItems(value)
        .map(v => v.name)
        .join(',') ?? '',
    toJson: ({ value }) => normalizeAttachmentCell(value),
  },
  jsonValue: {
    schema: FileCellJsonValueTypeSchema,
    type: () => t.attachment.instance(),
    isEmpty: ({ value }) => normalizeAttachmentCell(value).isEmpty,
  },
});
